import { DefaultModal } from 'UI';
import { DefaultModalProps } from 'UI/Modals/DefaultModal/DefaultModal';
import { TermEnum } from 'UI/PriceLabel/PriceLabel';
import {
  CourseDto,
  CourseDtoTypeEnum,
  CourseTypeEnum,
  ManualTypeEnum,
  PaymentDtoTypeEnum,
  SubscribeDtoTypeEnum,
} from 'api/generated';
import { BonusesModal } from 'components';
import { isMvpPurchase } from 'components/Modals/BonusesModal/bonuses';
import { useAsyncAction, useGoogleAnalytics, useNotifications } from 'hooks';
import { MonetizationLightModeProvider } from 'providers';
import { FC, useEffect, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import { CourseOptionsForPayment, PaymentOptions } from 'store/ducks/courses/types';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

import PricingTable from './PricingTable';
import { Products } from './components';
import TrialText from './components/LifetimeProducts/components/TrialText';
import type { ProductsProps as ProductsComponentProps } from './components/Products/types';

type ChooseProductModalProps = {
  course: CourseDto;
  openUnsubscribeModal: () => void;
} & DefaultModalProps;

const ChooseProductModal: FC<ChooseProductModalProps> = ({ course, openUnsubscribeModal, ...props }) => {
  const dispatch = useAppDispatch();
  const { errorToast } = useNotifications();
  const { getEventSubscriptionPaymentModalOpenedGA } = useGoogleAnalytics();

  // Check if this is a bundle
  const isBundle = (course.type as string) === 'bundle';

  // For certification bundles, treat them EXACTLY like certificate courses
  const isCertificate = course.type === CourseTypeEnum.Certificate;

  const [isShowBonusModal, setIsShowBonusModal] = useState(false);
  const [setActiveCourseAction, isCourseActivating] = useAsyncAction(actions.courses.setActiveCourse);
  const [cancelSubscriptionDowngradeAction] = useAsyncAction(actions.courses.cancelSubscriptionDowngrade);
  const [cancelSubscriptionTrialDowngradeAction] = useAsyncAction(actions.courses.cancelSubscriptionTrialDowngrade);
  const [, isSubscriptionUpdating] = useAsyncAction(actions.courses.changeCourseSubscribe);

  const direction = useAppSelector(selectors.direction.selectDirection);
  const isLandingPage = useAppSelector(selectors.auth.selectIsLandingPage);

  const directionId = direction?.id || '';
  const coursePrimaryColor =
    typeof (course as any)?.upsellPrimaryColor === 'string' ? (course as any).upsellPrimaryColor.trim() : '';
  const mainColor = coursePrimaryColor || direction?.mainColorHex;

  const getPricingAnimationUrl = (value: unknown) => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  };

  const parseOptionalJson = (value: unknown) => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  };

  const normalizeBonusesList = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const normalized = value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'name' in item) {
          const name = (item as { name?: unknown }).name;
          return typeof name === 'string' ? name : undefined;
        }
        return undefined;
      })
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

    return normalized.length > 0 ? normalized : undefined;
  };

  const mvpAnimationUrl = getPricingAnimationUrl((course as any)?.pricingAnimationGoldUrl);
  const allstarAnimationUrl = getPricingAnimationUrl((course as any)?.pricingAnimationSilverUrl);
  const rookieAnimationUrl = getPricingAnimationUrl((course as any)?.pricingAnimationBronzeUrl);
  const bonusesAnimationUrl = getPricingAnimationUrl((course as any)?.pricingAnimationBonusesUrl);
  const pointerAnimationUrl = getPricingAnimationUrl((course as any)?.pricingAnimationPointerUrl);
  const lottieOverrides: Partial<Record<'mvp' | 'allstar' | 'rookie' | 'gift' | 'thumb', string>> = {};

  if (mvpAnimationUrl) lottieOverrides.mvp = mvpAnimationUrl;
  if (allstarAnimationUrl) lottieOverrides.allstar = allstarAnimationUrl;
  if (rookieAnimationUrl) lottieOverrides.rookie = rookieAnimationUrl;
  if (bonusesAnimationUrl) lottieOverrides.gift = bonusesAnimationUrl;
  if (pointerAnimationUrl) lottieOverrides.thumb = pointerAnimationUrl;

  const pricingTableFeaturesData = parseOptionalJson((course as any)?.pricingTableFeaturesData);
  const pricingTableFeatures = parseOptionalJson((course as any)?.pricingTableFeatures);
  const pricingTableBonuses = normalizeBonusesList(parseOptionalJson((course as any)?.pricingTableBonuses));
  const pricingTableBonusesConfig = parseOptionalJson((course as any)?.pricingTableBonusesConfig);

  // Close modal on browser back/forward navigation (avoid adding listeners on every render).
  useEffect(() => {
    const handlePopState = () => {
      if (isLandingPage) {
        dispatch(actions.auth.setIsLandingPage(null));
      }
      dispatch(actions.courses.closeChooseProductModalOpen());
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, isLandingPage]);

  // For certification bundles, detect them by checking the bundle's payment structure
  // Bundle payment structure is returned from the API via the bundle_courses table
  // Fallback: If paymentStructure is not available, detect by pricing pattern (installment + purchase, no subscriptions)
  const bundlePaymentStructure = (course as any).paymentStructure;
  const hasInstallmentAndPurchase = !!(course.prices?.installment && course.prices?.purchase);
  const hasNoSubscriptions = !course.prices?.monthlySubscriptions && !course.prices?.annualSubscriptions;
  const hasInstallment = !!course.prices?.installment;
  const hasPurchase = !!course.prices?.purchase;
  const rawPaymentStructure = (() => {
    if (typeof bundlePaymentStructure === 'string') {
      return bundlePaymentStructure.trim();
    }

    if (typeof bundlePaymentStructure === 'object' && bundlePaymentStructure !== null) {
      const { id, value } = bundlePaymentStructure as { id?: string; value?: string };
      return id || value || '';
    }

    return '';
  })();
  const paymentStructure = rawPaymentStructure.toLowerCase();
  const normalizedPaymentStructure = paymentStructure.includes('study')
    ? 'study'
    : paymentStructure.includes('certification')
    ? 'certification'
    : paymentStructure;
  const hasStudySubscriptions = !!(course.prices?.monthlySubscriptions || course.prices?.annualSubscriptions);
  const isDefaultCourse = String(course.type || '').toLowerCase() === CourseTypeEnum.Default;
  const pricingTemplate = isDefaultCourse
    ? 'study-material'
    : normalizedPaymentStructure === 'study'
    ? 'study-material'
    : normalizedPaymentStructure === 'certification'
    ? 'certification'
    : hasStudySubscriptions
    ? 'study-material'
    : !hasInstallment && hasPurchase
    ? 'study-material'
    : 'certification';
  const isStudyMaterialCourse = pricingTemplate === 'study-material';
  const isCancellationScheduled = course.nextPayment?.type === 'Free';
  const isStudyMaterialsSubscription =
    course.type === CourseTypeEnum.Default &&
    isStudyMaterialCourse &&
    course.currentPayment?.type === PaymentDtoTypeEnum.Subscription &&
    !isCancellationScheduled;
  const isCertificationBundle = !!(
    isBundle &&
    (bundlePaymentStructure === 'certification' || (hasInstallmentAndPurchase && hasNoSubscriptions))
  );
  const shouldUseLifetimeProducts =
    course.type === CourseTypeEnum.Certificate || isCertificationBundle || isStudyMaterialCourse;

  const bundleCourseNames = (course as any)?.bundleCoursesAsBundle
    ? (course as any).bundleCoursesAsBundle
        .map((item: any) => item?.course?.name)
        .filter((name: unknown): name is string => typeof name === 'string' && name.trim().length > 0)
    : [];

  // Get pricing data from course.prices
  const monthlySubscriptions = course.prices?.monthlySubscriptions;
  const annualSubscriptions = course.prices?.annualSubscriptions;
  const purchase = course.prices?.purchase;
  const installment = course.prices?.installment;

  // Transform bundle pricing to match certification course tiered structure
  // Bundles might have flat pricing data that needs to be adapted to the tiered format
  const transformedPricing = (() => {
    if (isCertificationBundle) {
      // For bundles, we need to transform the pricing data to match the tiered structure
      // that LifetimeProducts expects (gold, silver, bronze)
      const isAlreadyTiered = (pricing: any) => {
        return (
          pricing && typeof pricing === 'object' && ('gold' in pricing || 'silver' in pricing || 'bronze' in pricing)
        );
      };
      let transformedInstallment = null;
      let transformedPurchase = null;
      if (installment) {
        if (isAlreadyTiered(installment)) {
          transformedInstallment = installment;
        } else {
          transformedInstallment = {
            gold: installment,
            silver: installment,
            bronze: installment,
          };
        }
      }
      if (purchase) {
        if (isAlreadyTiered(purchase)) {
          transformedPurchase = purchase;
        } else {
          transformedPurchase = {
            gold: purchase,
            silver: purchase,
            bronze: purchase,
          };
        }
      }
      return {
        monthlySubscriptions: null,
        annualSubscriptions: null,
        installment: transformedInstallment,
        purchase: transformedPurchase,
      };
    }
    // For non-bundles, just return the original structure
    return {
      monthlySubscriptions,
      annualSubscriptions,
      installment,
      purchase,
    };
  })();

  const selectCourseOptionsForPayment = (options: CourseOptionsForPayment) => {
    dispatch(actions.courses.selectCourseOptionsForPayment(options));
  };

  const closeChooseProductModalOpen = () => {
    if (isLandingPage) {
      dispatch(actions.auth.setIsLandingPage(null));
    }
    dispatch(actions.courses.closeChooseProductModalOpen());
  };

  const openPaymentModalOpen = () => {
    getEventSubscriptionPaymentModalOpenedGA();
    dispatch(actions.courses.openPaymentModalOpen());
  };

  const openCheckoutFromPricingTable = (options: CourseOptionsForPayment) => {
    dispatch(
      actions.courses.setPaymentOption(
        options.term === TermEnum.INSTALLMENT ? PaymentOptions.PAYMENT_PLAN : PaymentOptions.ONETIME,
      ),
    );
    selectCourseOptionsForPayment(options);
    openPaymentModalOpen();
  };

  const handleStartUnsubscribe = () => {
    const currentLevel = course.currentPayment?.level as SubscribeDtoTypeEnum | undefined;
    const tierKey = String(currentLevel || '').toLowerCase() as 'gold' | 'silver' | 'bronze';
    const currentMonthlyPrice = (transformedPricing.monthlySubscriptions as any)?.[tierKey];

    if (currentLevel && currentLevel !== SubscribeDtoTypeEnum.None) {
      selectCourseOptionsForPayment({
        term: TermEnum.MONTHLY,
        type: CourseDtoTypeEnum.Default,
        courseId: course.id,
        subscribeType: currentLevel,
        amount: Number(currentMonthlyPrice?.amount || 0),
        oldAmount: currentMonthlyPrice?.oldAmount,
        isUpdate: false,
        isDowngrade: false,
        couponCode: currentMonthlyPrice?.defaultCoupon?.promoCode,
        discount: currentMonthlyPrice?.cancellationPromotionTerms?.discount,
        discountType: currentMonthlyPrice?.cancellationPromotionTerms?.discountType,
        duration: currentMonthlyPrice?.cancellationPromotionTerms?.duration,
        durationInMonth: currentMonthlyPrice?.cancellationPromotionTerms?.durationInMonths,
      });
    }

    openUnsubscribeModal();
  };

  const setActiveCourse = async (courseId: string) => {
    try {
      await setActiveCourseAction({ courseId });
      closeChooseProductModalOpen();
    } catch {
      errorToast('Something went wrong');
    }
  };

  const cancelSubscriptionDowngrade = async (courseId: string, isCancelTrial?: boolean) => {
    try {
      isCancelTrial
        ? await cancelSubscriptionTrialDowngradeAction({ courseId })
        : await cancelSubscriptionDowngradeAction({ courseId });
      closeChooseProductModalOpen();
    } catch {
      errorToast('Something went wrong');
    }
  };

  const handlePricingTablePurchase = (planId: string, paymentType: 'onetime' | 'plan') => {
    const subscribeType =
      planId === 'ml'
        ? SubscribeDtoTypeEnum.Gold
        : planId === 'al'
        ? SubscribeDtoTypeEnum.Silver
        : SubscribeDtoTypeEnum.Bronze;
    const tierKey = subscribeType.toLowerCase() as 'gold' | 'silver' | 'bronze';

    const priceData = isStudyMaterialCourse
      ? paymentType === 'plan'
        ? (transformedPricing.monthlySubscriptions as any)?.[tierKey]
        : (transformedPricing.purchase as any)?.[tierKey]
      : paymentType === 'plan'
      ? (transformedPricing.installment as any)?.[tierKey]
      : (transformedPricing.purchase as any)?.[tierKey];

    if (!priceData?.amount) return;

    const options: CourseOptionsForPayment = {
      term:
        paymentType === 'plan' ? (isStudyMaterialCourse ? TermEnum.MONTHLY : TermEnum.INSTALLMENT) : TermEnum.LIFE_TIME,
      type: isStudyMaterialCourse ? CourseDtoTypeEnum.Default : CourseDtoTypeEnum.Certificate,
      courseId: course.id,
      subscribeType,
      amount: Number(priceData.amount),
      oldAmount: priceData.oldAmount,
      isUpdate: false,
      isDowngrade: false,
      couponCode: priceData.defaultCoupon?.promoCode,
      discount: priceData.cancellationPromotionTerms?.discount,
      discountType: priceData.cancellationPromotionTerms?.discountType,
      duration: priceData.cancellationPromotionTerms?.duration,
      durationInMonth: priceData.cancellationPromotionTerms?.durationInMonths,
      firstMonthDiscountId: priceData.firstMonthDiscountCouponId,
      firstMonthDiscountCode: priceData.firstMonthDiscountCoupon?.promoCode,
      firstMonthDiscountAmount: priceData.firstMonthDiscountCoupon?.discountAmount,
      firstMonthDiscountType: priceData.firstMonthDiscountCoupon?.discountType,
      firstMonthDiscountPromoCode: priceData.firstMonthDiscountCoupon?.promoCode,
    };

    openCheckoutFromPricingTable(options);
  };

  return (
    <MonetizationLightModeProvider>
      <>
        <DefaultModal
          {...props}
          id={'chooseProduct'}
          withTitleMargin={false}
          containerCSS={ContainerCSS}
          headerContainerCSS={headerContainerCSS}
          modalCSS={ModalCSS}
          closeButtonCSS={CloseCSS}
          titleCSS={titleCSS}>
          {shouldUseLifetimeProducts ? (
            <>
              <PricingTable
                accentColor={mainColor}
                courseId={course.id}
                directionId={directionId || undefined}
                courseName={course.name}
                bundleCourseNames={bundleCourseNames}
                isCertificate={isCertificate}
                hasBronzeAccess={
                  (course.currentPayment?.level as string) === 'bronze' ||
                  (course.currentPayment?.level as any) === SubscribeDtoTypeEnum.Bronze
                }
                headerConfig={{
                  title: `Choose your ${course.name} plan`,
                  subtitle: '12 payments (0% interest) – Or save 17% with one-time payment',
                  showBogoFeatureNote: isBundle,
                }}
                featuresData={pricingTableFeaturesData as any}
                features={pricingTableFeatures as any}
                bonuses={pricingTableBonuses as any}
                bonusesConfig={pricingTableBonusesConfig as any}
                prices={{
                  purchase: transformedPricing.purchase,
                  installment: transformedPricing.installment,
                  monthlySubscriptions: transformedPricing.monthlySubscriptions,
                }}
                pricingType={pricingTemplate}
                lottieAnimations={lottieOverrides}
                onPurchase={handlePricingTablePurchase}
              />

              {isStudyMaterialsSubscription && (
                <BottomActions>
                  <UnsubscribeButton onClick={handleStartUnsubscribe}>Unsubscribe</UnsubscribeButton>
                </BottomActions>
              )}

              {course.currentPayment?.level === 'none' && (
                <TrialText
                  isDowngrade={false}
                  cancelSubscriptionDowngrade={() => cancelSubscriptionDowngrade(course.id, true)}
                  currentPayment={course.currentPayment}
                  startTrial={() => setActiveCourse(course.id)}
                  isTrialStarting={isCourseActivating}
                  startUnsubscribeProcess={openUnsubscribeModal}
                />
              )}
            </>
          ) : (
            <>
              <TitleBlock>
                <ModalTitle>{`Choose your ${course.name} plan`}</ModalTitle>
              </TitleBlock>
              <Products
                // toggleSubscription={toggleSubscription}
                cancelSubscriptionDowngrade={cancelSubscriptionDowngrade}
                currentPayment={course.currentPayment}
                nextPayment={course.nextPayment}
                isCertificate={isCertificate}
                setIsShowBonusModal={setIsShowBonusModal}
                mainColor={mainColor}
                // @ts-ignore
                directionId={directionId}
                purchasePrice={transformedPricing.purchase as ProductsComponentProps['purchasePrice']}
                annualPrices={transformedPricing.annualSubscriptions as ProductsComponentProps['annualPrices']}
                monthlyPrices={transformedPricing.monthlySubscriptions as ProductsComponentProps['monthlyPrices']}
                installmentPrice={transformedPricing.installment as ProductsComponentProps['installmentPrice']}
                courseId={course.id}
                isTrialStarting={isCourseActivating}
                startTrial={setActiveCourse}
                // startUnsubscribeProcess={startUnsubscribeProcess}
                isSubscriptionUpdating={isSubscriptionUpdating}
                // paymentsDescriptions={content?.monthlySubscriptions as ManualPaymentsDescriptions}
                // mostEffectiveLabel={content?.mostEffective}
                _isBundle={isBundle}
              />
            </>
          )}
        </DefaultModal>
        <BonusesModal
          isOpen={isShowBonusModal}
          onClose={() => setIsShowBonusModal(false)}
          manualType={
            course.currentPayment?.level === 'gold'
              ? ManualTypeEnum.BonusSectionUpgrade
              : ManualTypeEnum.BonusSectionDefault
          }
          course={course}
          isMember={isMvpPurchase(course.currentPayment)}
          hideUpgrade
        />
      </>
    </MonetizationLightModeProvider>
  );
};

export default ChooseProductModal;

const ContainerCSS = css`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 22px 22px;
  border-radius: 0;
  overflow: auto;

  ${respondToWidth.sm`
    width:100%;
    padding-left: 25px;
    padding-right: 17px;
    padding-bottom: 32px;
  `}

  ${respondToWidth.s`
    height:100%;
    padding: 0;
    border-radius: 0;
  `}
`;

const ModalCSS = css`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transform: none;
  margin-right: 0;
  width: 100vw;
  height: 100vh;
  padding: 0;

  ${respondToWidth.sm`
    bottom: unset !important;
    left: 0 !important;
    right: 0 !important;
    transform: none !important;
  `}
`;

const BottomActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 12px;

  ${respondToWidth.sm`
    margin-top: 8px;
  `}
`;

const UnsubscribeButton = styled.button`
  ${({ theme: { colors, typography } }) => css`
    border: none;
    background: transparent;
    color: ${colors.primary[1]};
    cursor: pointer;
    padding: 0;
    ${typography.body_basic_semibold_14};

    &:hover {
      text-decoration: underline;
    }
  `}
`;

const headerContainerCSS = css`
  margin-bottom: 0px;

  ${respondToWidth.s`
    justify-content: center;
    margin-top: 24px !important;
    padding-top: 24px !important;
  `}
`;

const titleCSS = css`
  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  @media (max-width: 1180px) {
    margin-bottom: 105px;
  }

  ${respondToWidth.s`
    margin-top: 24px !important;
    text-align: center;
    margin-bottom: 90px;
  `};
`;

const CloseCSS = css`
  top: 18px;
  right: 18px;
`;

const TitleBlock = styled.div`
  padding-right: 44px;
  margin-bottom: 18px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  margin-top: 60px;
  color: ${({ theme }) => theme.colors.neutrals[1]};
  font-weight: 900;
  font-size: 26px;
  text-align: center;
`;

// const Container = styled.div`
//   width: 100%;
//   height: 100%;

//   display: flex;
//   justify-content: center;
//   align-items: center;
// `;
